// Canonical display of dates and times.
//
// Every formatter here pins `timeZone: "Europe/Copenhagen"`. That is the whole
// point of the module, not a detail: `toLocaleString("da-DK")` without a
// timeZone resolves against the *host* zone, and the host is UTC in production
// (Vercel) and in CI. Before this module five call sites pinned the zone and
// fourteen did not, so the same timestamp rendered an hour off — and for
// anything between 22:00 and midnight UTC, a *day* off — depending on which
// copy of the formatter the page happened to reach for. A lead created at
// 22:30 UTC on 3 August is a Danish 4 August, and the admin lead table said
// 3 August.
//
// Pinning also keeps a server-rendered string identical to the one the client
// produces on rehydration, which the un-pinned copies could not promise for a
// visitor outside Denmark.
//
// The site is Danish-only, so the locale is fixed too.

export const DANISH_TIME_ZONE = "Europe/Copenhagen";

const DATE = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: DANISH_TIME_ZONE,
});

const DAY_MONTH = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "long",
  timeZone: DANISH_TIME_ZONE,
});

const DAY_MONTH_TIME = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "long",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: DANISH_TIME_ZONE,
});

const TIME = new Intl.DateTimeFormat("da-DK", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: DANISH_TIME_ZONE,
});

const MONTH_YEAR = new Intl.DateTimeFormat("da-DK", {
  month: "long",
  year: "numeric",
  timeZone: DANISH_TIME_ZONE,
});

const SHORT_DATE_TIME = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: DANISH_TIME_ZONE,
});

const DATE_TIME = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: DANISH_TIME_ZONE,
});

// Two-digit everything: the ledger's date column sits next to two numeric
// money columns and has to stay narrow.
const COMPACT_DATE_TIME = new Intl.DateTimeFormat("da-DK", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: DANISH_TIME_ZONE,
});

const NUMERIC_DATE = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "numeric",
  year: "numeric",
  timeZone: DANISH_TIME_ZONE,
});

const TIMESTAMP = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  timeZone: DANISH_TIME_ZONE,
});

type DateInput = Date | string;

// An <input type="date"> value is yyyy-mm-dd by spec whatever the user's
// locale — the browser renders the Danish display itself. da-DK formats
// "04.08.2026", which the element cannot parse, and a min/max it cannot parse
// is dropped without a word. en-CA and sv-SE are the numeric-ISO locales; DANISH_TIME_ZONE
// is what makes it the Danish day rather than the host's.
const DATE_INPUT = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: DANISH_TIME_ZONE,
});

// An unparseable value falls back to whatever was handed in, rather than
// throwing. These run inside page and email renders, where a bad timestamp
// should cost one ugly line and not the whole document.
function apply(formatter: Intl.DateTimeFormat, value: DateInput): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatter.format(date);
}

const HOUR = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  hourCycle: "h23",
  timeZone: DANISH_TIME_ZONE,
});

/** 0-23 — the Danish wall-clock hour, for time-of-day rules rather than display. */
export function danishHour(at: Date): number {
  return Number(HOUR.format(at));
}

/** "3. marts 2026" — a calendar date in prose. */
export function formatDate(value: DateInput): string {
  return apply(DATE, value);
}

/** "14.12" — a clock time on the current day. */
export function formatTime(value: DateInput): string {
  return apply(TIME, value);
}

/** "3. marts" — a date whose year is obvious from context. */
export function formatDayMonth(value: DateInput): string {
  return apply(DAY_MONTH, value);
}

/** "3. marts 14.30" — a deadline, where the time of day is the point. */
export function formatDayMonthTime(value: DateInput): string {
  return apply(DAY_MONTH_TIME, value);
}

/**
 * "3. marts – 5. marts" — a start window. Collapses to a single date when both
 * ends fall on the same day.
 */
export function formatDayMonthRange(earliest: DateInput, latest: DateInput): string {
  const from = formatDayMonth(earliest);
  const to = formatDayMonth(latest);
  return from === to ? from : `${from} – ${to}`;
}

/** "august 2026" — a freshness stamp, where the day would imply false precision. */
export function formatMonthYear(value: DateInput): string {
  return apply(MONTH_YEAR, value);
}

/** "3. mar. 14.30" — compact, for timelines and table cells. */
export function formatShortDateTime(value: DateInput): string {
  return apply(SHORT_DATE_TIME, value);
}

/** "3. mar. 2026 14.30" — as above, where the year still matters. */
export function formatDateTime(value: DateInput): string {
  return apply(DATE_TIME, value);
}

/** "03.03.26 14.30" — the ledger's date column, kept narrow. */
export function formatCompactDateTime(value: DateInput): string {
  return apply(COMPACT_DATE_TIME, value);
}

/** "3.3.2026" — dense admin tables, where the column is a date and nothing more. */
export function formatNumericDate(value: DateInput): string {
  return apply(NUMERIC_DATE, value);
}

/** "3.3.2026, 14.30.15" — audit logs and any row where the time of day matters. */
export function formatTimestamp(value: DateInput): string {
  return apply(TIMESTAMP, value);
}

/** "2026-03-03" — value, min or max of an `<input type="date">`, as the Danish calendar day. */
export function formatDateInput(value: DateInput): string {
  return apply(DATE_INPUT, value);
}

/**
 * "2026-09-05" — the wire form for a date-only value. A `@db.Date` column comes
 * back as UTC midnight, so UTC is the column's own zone here, not the host's.
 */
export function formatIsoDate(value: DateInput): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().slice(0, 10);
}
